import React from "react";
import { CheckCircle2, ExternalLink, FileText, Send, XCircle } from "lucide-react";
import { transmittedSummary, formatDateTime } from "utilities/stratusPreflight";
import CopyLinkButton from "components/MidApplications/CopyLinkButton";

/**
 * SOUND-776 — the right-rail block for a MID application that has been sent to
 * Kurv. Read-only; replaces the Transmit action once `externalApplicationId` is
 * set. Rail chrome per the SOUND-643 record layout: `surface p-4` with the small
 * uppercase heading.
 *
 * Renders nothing for an untransmitted record, so the caller can mount it
 * unconditionally. `externalAgentUrl`, `externalApplicationUrl` and `documentUpload` are
 * internal-only on the API and absent for other callers — every part of this
 * panel tolerates them missing (the page also mounts it for internal only).
 */
export default function StratusTransmittedPanel({ application }) {
  const s = transmittedSummary(application);
  if (!s) return null;
  const docs = s.documents;

  return (
    <section className="surface p-4">
      <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <Send className="h-3.5 w-3.5" />
        Kurv submission
      </h3>

      <div className="mb-3 flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-success" />
        <div className="min-w-0">
          <div className="text-sm font-medium text-foreground">Submitted to Kurv</div>
          {s.transmittedAt && (
            <div className="text-xs text-muted-foreground">{formatDateTime(s.transmittedAt)}</div>
          )}
        </div>
      </div>

      <dl className="space-y-2 text-sm">
        <div>
          <dt className="text-xs text-muted-foreground">MCC sent</dt>
          <dd className="text-foreground">{s.mcc || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Pricing template</dt>
          <dd className="text-foreground">{s.pricingTemplate || "—"}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Documents</dt>
          <dd>
            {!docs ? (
              <span className="text-muted-foreground/60">No upload recorded</span>
            ) : (
              <>
                <span className="text-foreground">
                  {docs.uploaded.length} uploaded
                  {docs.errors.length > 0 && (
                    <span className="text-destructive"> · {docs.errors.length} failed</span>
                  )}
                  {docs.skipped.length > 0 && (
                    <span className="text-warning"> · {docs.skipped.length} not sent</span>
                  )}
                </span>
                {docs.uploaded.length > 0 && (
                  <ul className="mt-1 space-y-0.5">
                    {docs.uploaded.map((d, i) => (
                      <li key={`${d.filename}-${i}`} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <FileText className="h-3 w-3 shrink-0" />
                        <span className="truncate">{d.filename || d.type || "Document"}</span>
                        {d.stratusCategory && <span className="shrink-0">· {d.stratusCategory}</span>}
                      </li>
                    ))}
                  </ul>
                )}
                {docs.errors.length > 0 && (
                  <ul className="mt-2 space-y-1 rounded-md bg-destructive/5 p-2 ring-1 ring-destructive/30">
                    {docs.errors.map((e, i) => (
                      <li key={i} className="flex items-start gap-1.5 text-xs">
                        <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
                        <span className="text-foreground">
                          {e.filename ? <span className="font-medium">{e.filename}: </span> : null}
                          {e.message}
                          {e.stratusCategory && (
                            <span className="text-muted-foreground"> ({e.stratusCategory})</span>
                          )}
                        </span>
                      </li>
                    ))}
                    <li className="text-[11px] text-muted-foreground">Upload these in the Kurv portal.</li>
                  </ul>
                )}
                {docs.skipped.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {docs.skipped.map((d, i) => (
                      <li key={`${d.filename}-${i}`} className="text-xs text-muted-foreground">
                        Not sent: <span className="text-foreground">{d.filename || d.type || "Document"}</span>
                        {d.reason && ` — ${d.reason}`}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </dd>
        </div>
      </dl>

      {/* Two links (CPO): the AGENT link opens; the MERCHANT link (the one a
          merchant uses to review and sign) is copy-only. No agent link means
          no primary button — never the merchant link in its place. */}
      {s.agentUrl && (
        <a
          href={s.agentUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-outline mt-3 w-full justify-center gap-2"
        >
          <ExternalLink className="h-4 w-4" />
          Open in Kurv
        </a>
      )}
      <CopyLinkButton url={s.merchantUrl} className={`w-full ${s.agentUrl ? "mt-1" : "mt-3"}`} />
    </section>
  );
}
